import * as React from 'react';

import { $setAutoFocusRowId } from '@rematter/modules/trade/_state/materialsInterface.state';
import { MaterialSymbol, Row } from '@rematter/ui-web';
import { useSetAtom } from 'jotai';
import { LucideCog } from 'lucide-react';

import { CatalyticConverterModal } from '../../CatalyticConverterModal';
import { ItemFieldProps } from '../_types';
import { useDialogModalStyle } from '../hooks/useDialogModalStyle';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SelectMaterialTouch } from '~/shared/components/select-fields/SelectMaterialTouch';
import { useUIPreferences } from '~/shared/hooks';

//
// ⚛️ ⚛️ ⚛️ CATALYTIC CONVERTER ITEM FIELD ⚛️ ⚛️ ⚛️
//
export const CatalyticConverterItemField: React.FC<ItemFieldProps> = ({
  lineItem,
  symbolDescription,
  disabled,
  onUpdateItem,
}) => {
  // Translations
  const t = useTranslations();
  // Atoms
  const setAutoFocusRowId = useSetAtom($setAutoFocusRowId);

  // States

  const [selectedMnemonic, setSelectedMnemonic] = React.useState<
    string | undefined
  >();
  const [materialModalOpen, setMaterialModalOpen] = React.useState(false);

  // Data
  const { touchModeEnabled } = useUIPreferences();

  const modalStyle = useDialogModalStyle();

  // Callbacks
  const handleEntityResolved = React.useCallback(
    (entity: { itemGroup?: { id?: string } | null; skuNumber?: string }) => {
      setSelectedMnemonic(entity?.skuNumber ?? undefined);
    },
    [],
  );

  const handleCatalyticOpenChange = React.useCallback(() => {
    setMaterialModalOpen((prev) => !prev);
  }, []);

  const handleCatalyticItemSelect = React.useCallback(
    (itemId?: string) => {
      onUpdateItem?.(lineItem.id, { itemId });

      setAutoFocusRowId(null);
    },
    [setMaterialModalOpen],
  );

  return (
    <Row className="@CatalyticConverterItemField h-full items-center gap-2">
      <MaterialSymbol
        description={symbolDescription || selectedMnemonic}
        color="slate"
        className="w-[35px] max-w-[35px]"
        symbolIcon={<LucideCog size={16} />}
      />

      <SelectMaterialTouch
        className="h-full min-h-full flex-1"
        prefix={t.scaleTicketPro.inventoryItemField.prefixConverter}
        placeholder={t.scaleTicketPro.inventoryItemField.placeholder}
        value={lineItem.itemId}
        onEntityResolved={handleEntityResolved}
        onChange={(itemId) =>
          onUpdateItem?.(lineItem.id, { itemId: itemId ?? null })
        }
        isDisabled={disabled}
        onTriggerClick={
          touchModeEnabled ? handleCatalyticOpenChange : undefined
        }
      />
      {touchModeEnabled && (
        <CatalyticConverterModal
          isOpen={materialModalOpen}
          mode="add"
          initialSelectedItemId={lineItem.itemId}
          lineItemId={lineItem.itemId}
          touchModeEnabled={touchModeEnabled}
          onOpenChange={handleCatalyticOpenChange}
          onSelectItem={handleCatalyticItemSelect}
          modalStyle={modalStyle}
        />
      )}
    </Row>
  );
};
